/**
 * Color palette for trace visualization.
 *
 * Spec: dev/projects/sl2-sstv2-live-trace.md §4.2
 *
 * Each step "kind" maps to a fill color (the node body) and each status maps
 * to a border treatment. Kept in one module so diagram + drawer + chips stay
 * visually consistent.
 */
import type { TraceStatus, TraceStepKind, TraceStepStatus } from "./types"

export type KindStyle = {
  /** Background color for the diagram node body / kind chip. */
  background: string
  /** Foreground (text) color, paired with background. */
  color: string
  /** A short user-facing label. */
  label: string
}

const KIND_STYLES: Record<TraceStepKind, KindStyle> = {
  entry: {
    background: "#1f2937",
    color: "#f9fafb",
    label: "Entry",
  },
  validation: {
    background: "#9ca3af",
    color: "#111827",
    label: "Validation",
  },
  routing: {
    background: "#a78bfa",
    color: "#1f1147",
    label: "Routing",
  },
  lookup: {
    background: "#fde68a",
    color: "#5b3a00",
    label: "Lookup",
  },
  "ddb-read": {
    background: "#5eead4",
    color: "#0f3a35",
    label: "DDB read",
  },
  "ddb-write": {
    background: "#60a5fa",
    color: "#0c2456",
    label: "DDB write",
  },
  "partner-api": {
    background: "#fb923c",
    color: "#3b1c00",
    label: "Partner API",
  },
  iot: {
    background: "#c084fc",
    color: "#2c0a4f",
    label: "IoT",
  },
  "sqs-out": {
    background: "#f472b6",
    color: "#3a0a26",
    label: "SQS out",
  },
  "lambda-invoke": {
    background: "#facc15",
    color: "#3a2c00",
    label: "Lambda",
  },
  notification: {
    background: "#34d399",
    color: "#053723",
    label: "Notification",
  },
  terminal: {
    background: "#1f2937",
    color: "#f9fafb",
    label: "Terminal",
  },
  downstream: {
    background: "#94a3b8",
    color: "#0f172a",
    label: "Downstream",
  },
}

export const getKindStyle = (kind: TraceStepKind): KindStyle =>
  KIND_STYLES[kind] ?? {
    background: "#e5e7eb",
    color: "#111827",
    label: kind,
  }

export type StepBorderStyle = {
  borderColor: string
  borderStyle: "solid" | "dashed"
  borderWidth: number
}

export const getStepBorderStyle = (
  status: TraceStepStatus,
): StepBorderStyle => {
  switch (status) {
    case "succeeded":
      return { borderColor: "#22c55e", borderStyle: "solid", borderWidth: 2 }
    case "failed":
      return { borderColor: "#ef4444", borderStyle: "solid", borderWidth: 2 }
    case "skipped":
      return { borderColor: "#d1d5db", borderStyle: "solid", borderWidth: 2 }
    case "started":
      return { borderColor: "#9ca3af", borderStyle: "dashed", borderWidth: 2 }
  }
}

export type TraceStatusStyle = {
  background: string
  color: string
  label: string
  /** When true, render with a pulsing animation. */
  pulse?: boolean
}

export const getTraceStatusStyle = (status: TraceStatus): TraceStatusStyle => {
  switch (status) {
    case "succeeded":
      return { background: "#dcfce7", color: "#14532d", label: "Succeeded" }
    case "failed":
      return { background: "#fee2e2", color: "#7f1d1d", label: "Failed" }
    case "partial":
      return { background: "#fef3c7", color: "#78350f", label: "Partial" }
    case "in-progress":
      return {
        background: "#fef9c3",
        color: "#713f12",
        label: "In progress",
        pulse: true,
      }
    case "discarded":
      return { background: "#e5e7eb", color: "#374151", label: "Discarded" }
  }
}

/** MUI Chip color name for a trace status (used in tables/chips). */
export const getMuiTraceStatusColor = (
  status: TraceStatus,
):
  | "success"
  | "error"
  | "warning"
  | "info"
  | "default" => {
  switch (status) {
    case "succeeded":
      return "success"
    case "failed":
      return "error"
    case "partial":
      return "warning"
    case "in-progress":
      return "info"
    case "discarded":
      return "default"
  }
}

/** MUI Chip color name for a step status. */
export const getMuiStepStatusColor = (
  status: TraceStepStatus,
): "success" | "error" | "default" | "info" => {
  switch (status) {
    case "succeeded":
      return "success"
    case "failed":
      return "error"
    case "skipped":
      return "default"
    case "started":
      return "info"
  }
}
